import React, { useEffect, useState } from 'react';

import {
  ExclamationCircleOutlined,
  EyeOutlined,
  LockOutlined,
  MailOutlined,
  MoreOutlined,
  PlusOutlined,
  UnlockOutlined,
} from '@ant-design/icons';
import {
  Button,
  Card,
  Col,
  Dropdown,
  GetProp,
  MenuProps,
  Modal,
  Row,
  Table,
  TablePaginationConfig,
  TableProps,
  Tag,
  Tooltip,
  message,
} from 'antd';
import Search from 'antd/es/input/Search';
import Column from 'antd/es/table/Column';
import { SearchProps } from 'antd/lib/input';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { ApiErrorData } from 'models/ApiResponse';
import { Company } from 'models/Company';
import { Country } from 'models/Country';
import { Role } from 'models/Role';
import { User } from 'models/User';

import { CompanyService } from 'services/CompanyService';
import { CountryService } from 'services/CountryService';
import { RoleService } from 'services/RoleService';
import { UserService } from 'services/UserService';

import { FunctionsHelper } from 'utils/FunctionsHelper';

import Layout from '../../../components/Layout/Layout';
import appI18n from '../../../config/i18n';
import DetailUserModal from './Components/DetailUserModal';
import NewUserModal from './Components/NewUserModal';

export const USER_TYPES = {
  SUPER_ADMIN: 'SUPER_ADMIN',
  ADMIN: 'ADMIN',
  REGULAR_ACCOUNT: 'REGULAR_ACCOUNT',
};

interface TableParams {
  pagination?: TablePaginationConfig;
  sortField?: string;
  sortOrder?: string;
  filters?: Parameters<GetProp<TableProps, 'onChange'>>[1];
}

const PAGE_SIZE = 10;

function SuperUsers() {
  const { t } = useTranslation(undefined, { i18n: appI18n });
  const [messageApi, contextHolder] = message.useMessage();
  const [modal, contextModalHolder] = Modal.useModal();
  const authUser: User = useSelector((state: any) => state.auth);
  const [tableLoading, setTableLoading] = useState(true);
  const [dataSource, setDataSource] = useState<User[]>([]);
  const [filteredDataSource, setFilteredDataSource] = useState<User[]>([]);
  const [tableParams, setTableParams] = useState<TableParams>({
    pagination: {
      current: 1,
      pageSize: PAGE_SIZE,
    },
  });
  const [title] = useState<string>('');
  const [roles, setRoles] = useState<Role[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);

  const [selectedRow, setSelectedRow] = useState<User | undefined>(undefined);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isNewResourceModalOpen, setIsNewResourceModalOpen] = useState(false);

  /* Filters */
  const [term, setTerm] = useState('');

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    fetchData(tableParams.pagination?.current);
  }, [
    tableParams.pagination?.current,
    tableParams.pagination?.pageSize,
    tableParams.sortField,
    tableParams.sortOrder,
  ]);

  useEffect(() => {
    fetchData(1);
  }, [term]);

  const init = async () => {
    setTableLoading(true);

    const [roleResponse, companyResponse, countryResponse] = await Promise.all([
      RoleService.getAll(),
      CompanyService.getAll(),
      CountryService.getAll(),
      fetchDataFromServer(false),
    ]);

    if (roleResponse.success) {
      setRoles(roleResponse.data as Role[]);
    } else {
      const error = roleResponse.data as ApiErrorData;
      messageApi.error(
        (error.message as string) ||
          t('scenes.settings.super_users.scene.errors.fetch_roles'),
        3.5
      );
    }

    if (companyResponse.success) {
      setCompanies(companyResponse.data as Company[]);
    } else {
      const error = companyResponse.data as ApiErrorData;
      messageApi.error(
        (error.message as string) ||
          t('scenes.settings.super_users.scene.errors.fetch_companies'),
        3.5
      );
    }

    if (countryResponse.success) {
      setCountries(countryResponse.data as Country[]);
    } else {
      const error = countryResponse.data as ApiErrorData;
      messageApi.error(
        (error.message as string) ||
          t('scenes.settings.super_users.scene.errors.fetch_countries'),
        3.5
      );
    }

    setTableLoading(false);
  };

  const onSearch: SearchProps['onSearch'] = (value) => {
    setTerm(value);
  };

  const createResourceModal = () => {
    setIsNewResourceModalOpen(true);
  };

  const showDetailResource = async (row: User) => {
    setSelectedRow(row);
    setIsDetailModalOpen(true);
  };

  const handleCloseNewResourceModal = () => {
    setIsNewResourceModalOpen(false);
  };

  const handleCloseShowDetailResourceModal = () => {
    setSelectedRow(undefined);
    setIsDetailModalOpen(false);
  };

  const getDropdownMenu = (row: User): MenuProps['items'] => {
    const menus: MenuProps['items'] = [];
    let itemNro = 1;

    menus.push({
      key: `${itemNro++}`,
      label: (
        <span>
          <EyeOutlined style={{ marginRight: '8px' }} />{' '}
          {t('scenes.settings.super_users.scene.dropdown.view_detail')}
        </span>
      ),
      onClick: () => {
        showDetailResource(row);
      },
    });

    menus.push({
      key: `${itemNro++}`,
      label: (
        <span>
          <MailOutlined style={{ marginRight: '8px' }} />{' '}
          {t(
            'scenes.settings.super_users.scene.dropdown.reset_password_and_notify'
          )}
        </span>
      ),
      onClick: () => {
        resetAndResendPassword(row);
      },
    });

    if (row.disabledAt) {
      menus.push({
        key: `${itemNro++}`,
        label: (
          <span>
            <UnlockOutlined style={{ marginRight: '8px' }} />{' '}
            {t('scenes.settings.super_users.scene.dropdown.activate_user')}
          </span>
        ),
        onClick: async () => {
          await setUserAsActive(row);
        },
      });
    } else {
      menus.push({
        key: `${itemNro++}`,
        label: (
          <span>
            <LockOutlined style={{ marginRight: '8px' }} />{' '}
            {t('scenes.settings.super_users.scene.dropdown.deactivate_user')}
          </span>
        ),
        onClick: async () => {
          await setUserAsInactive(row);
        },
        danger: true,
      });
    }

    return menus;
  };

  const setUserAsInactive = async (user: User) => {
    setTableLoading(true);
    const response = await UserService.disableUser(user.id);

    if (response.success) {
      await fetchDataFromServer();
      messageApi.success(
        t(
          'scenes.settings.super_users.scene.messages.deactivate_user_success',
          {
            email: user.email,
            id: user.id,
          }
        )
      );
    } else {
      const error = response.data as ApiErrorData;
      messageApi.error(
        (error.message as string) ||
          t('scenes.settings.super_users.scene.errors.deactivate_user_failed'),
        3.5
      );
    }

    setTableLoading(false);
  };

  const setUserAsActive = async (user: User) => {
    setTableLoading(true);
    const response = await UserService.enableUser(user.id);

    if (response.success) {
      await fetchDataFromServer();
      messageApi.success(
        t('scenes.settings.super_users.scene.messages.activate_user_success', {
          email: user.email,
          id: user.id,
        })
      );
    } else {
      const error = response.data as ApiErrorData;
      messageApi.error(
        (error.message as string) ||
          t('scenes.settings.super_users.scene.errors.activate_user_failed'),
        3.5
      );
    }

    setTableLoading(false);
  };

  const resetAndResendPassword = async (user: User) => {
    modal.confirm({
      title: t(
        'scenes.settings.super_users.scene.reset_password_confirm.title'
      ),
      icon: <ExclamationCircleOutlined />,
      content: t(
        'scenes.settings.super_users.scene.reset_password_confirm.content',
        {
          name: user.name,
          lastname: user.lastname,
          email: user.email,
        }
      ),
      okText: t('components.layout.profile_menu.logout_confirm.ok'),
      cancelText: t('components.layout.profile_menu.logout_confirm.cancel'),
      onOk: async () => {
        setTableLoading(true);
        const response = await UserService.resetAndSendNewPassword(user.id);

        if (response.success) {
          await fetchDataFromServer();
          messageApi.success(
            t(
              'scenes.settings.super_users.scene.messages.reset_password_success',
              { id: user.id }
            )
          );
        } else {
          const error = response.data as ApiErrorData;
          messageApi.error(
            (error.message as string) ||
              t(
                'scenes.settings.super_users.scene.errors.reset_password_failed'
              ),
            3.5
          );
        }

        setTableLoading(false);
      },
    });
  };

  const handleTableChange: TableProps<User>['onChange'] = (
    pagination,
    filters,
    sorter
  ) => {
    const sorterLocal = sorter as any;

    setTableParams({
      pagination,
      filters,
      sortOrder:
        sorterLocal.order && sorterLocal.columnKey
          ? sorterLocal.order
          : undefined,
      sortField:
        sorterLocal.order && sorterLocal.columnKey
          ? sorterLocal.columnKey
          : undefined,
    });

    if (pagination.pageSize !== tableParams.pagination?.pageSize) {
      setFilteredDataSource([]);
    }
  };

  const fetchDataFromServer = async (withLoading = false) => {
    if (withLoading) {
      setTableLoading(true);
    }
    const response = await UserService.getAllSuperUsers();

    if (response.success) {
      setDataSource(response.data as User[]);
      fetchData(1, response.data as User[]);
    } else {
      const error = response.data as ApiErrorData;
      messageApi.error(
        (error.message as string) ||
          t('scenes.settings.super_users.scene.errors.fetch_users'),
        3.5
      );
    }

    if (withLoading) {
      setTableLoading(false);
    }
  };

  const fetchData = (currentPage = 1, data?: User[]) => {
    const termFilter = FunctionsHelper.normalizeText(term.trim());

    let filteredData = data !== undefined ? data : [...dataSource];

    filteredData = filteredData.sort((a, b) => {
      return b.id - a.id;
    });

    if (termFilter.length > 0) {
      filteredData = filteredData.filter((record) => {
        return (
          FunctionsHelper.normalizeText(
            `${record.name} ${record.lastname}`
          ).includes(termFilter) ||
          FunctionsHelper.normalizeText(record.email).includes(termFilter)
        );
      });
    }

    const pageSize = tableParams.pagination?.pageSize || PAGE_SIZE;
    const total = filteredData.length;
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedData = filteredData.slice(startIndex, endIndex);

    setFilteredDataSource(paginatedData);
    setTableParams({
      ...tableParams,
      pagination: {
        ...tableParams.pagination,
        current: currentPage,
        total: total,
      },
    });
  };

  const createNewUser = async (
    name: string,
    lastname: string,
    email: string,
    dialCode: string | null,
    phone: string | null,
    roleIds: number[],
    companyId: number,
    userType: 'SUPER_ADMIN' | 'ADMIN',
    restrictedToCountries: boolean,
    allowedCountries: string[]
  ) => {
    setTableLoading(true);

    const response = await UserService.createActiveUserAndSendPassword({
      name,
      lastname,
      email,
      dialCode,
      phone,
      roleIds,
      companyId,
      userType,
      restrictedToCountries,
      allowedCountries,
    });

    if (response.success) {
      await fetchDataFromServer(true);
      messageApi.success(
        t('scenes.settings.super_users.scene.messages.create_user_success', {
          name,
          lastname,
          id: (response.data as { createdId: number }).createdId,
        })
      );
      handleCloseNewResourceModal();
    } else {
      const error = response.data as ApiErrorData;
      messageApi.error(
        (error.message as string) ||
          t('scenes.settings.super_users.scene.errors.create_user_failed'),
        3.5
      );
    }

    setTableLoading(false);
  };

  const editUser = async (
    id: number,
    name: string,
    lastname: string,
    email: string,
    dialCode: string | null,
    phone: string | null,
    roleIds: number[],
    restrictedToCountries: boolean,
    allowedCountries: string[]
  ) => {
    setTableLoading(true);

    const response = await UserService.update(id, {
      name,
      lastname,
      email,
      dialCode,
      phone,
      roleIds,
      restrictedToCountries,
      allowedCountries,
    });

    if (response.success) {
      await fetchDataFromServer(true);
      messageApi.success(
        t('scenes.settings.super_users.scene.messages.update_user_success', {
          name,
          lastname,
          id,
        })
      );
      handleCloseShowDetailResourceModal();
    } else {
      const error = response.data as ApiErrorData;
      messageApi.error(
        (error.message as string) ||
          t('scenes.settings.super_users.scene.errors.update_user_failed'),
        3.5
      );
    }

    setTableLoading(false);
  };

  const getDialCode = (countryCode: string): string => {
    let dialCode = '+56';

    switch (countryCode.toLowerCase()) {
      case 'pe':
        dialCode = '+51';
        break;
      case 'usa':
        dialCode = '+1';
        break;
      case 'mx':
        dialCode = '+52';
        break;
      case 'co':
        dialCode = '+57';
        break;
    }

    return dialCode;
  };

  const getUserTypeLabel = (userType: string) => {
    switch (userType) {
      case USER_TYPES.SUPER_ADMIN:
        return t(
          'scenes.settings.super_users.scene.table.user_type.super_admin'
        );
      case USER_TYPES.ADMIN:
        return t('scenes.settings.super_users.scene.table.user_type.admin');
      default:
        return userType;
    }
  };

  return (
    <>
      {contextHolder}
      {contextModalHolder}

      <Layout title={title}>
        <Card>
          <div className="filter-container">
            <Row gutter={[16, 12]} align="middle">
              <Col flex="none">
                <label className="filter-search-container">
                  {t('scenes.settings.super_users.scene.filters.label')}{' '}
                  <Search
                    placeholder={t(
                      'scenes.settings.super_users.scene.filters.search_placeholder_name_or_email'
                    )}
                    onSearch={onSearch}
                    disabled={tableLoading}
                    allowClear
                  />
                </label>
              </Col>
              <Col flex="auto" />
              <Col flex="none" className="filter-buttons-container">
                <Button
                  type="primary"
                  onClick={createResourceModal}
                  disabled={tableLoading}
                >
                  <PlusOutlined />{' '}
                  {t('scenes.settings.super_users.scene.buttons.create_user')}
                </Button>
              </Col>
            </Row>
          </div>
        </Card>

        <Card style={{ marginTop: '12px' }}>
          <Table<User>
            dataSource={filteredDataSource}
            bordered
            loading={tableLoading}
            size="small"
            scroll={{ x: 1300 }}
            pagination={tableParams.pagination}
            rowKey={(record) => record.id}
            onChange={handleTableChange}
          >
            <Column
              title={t('scenes.settings.super_users.scene.table.columns.id')}
              dataIndex="id"
              key="id"
              width={60}
            />

            <Column
              title={t('scenes.settings.super_users.scene.table.columns.name')}
              key="fullname"
              render={(row: User) => (
                <span>
                  {row.name} {row.lastname}
                </span>
              )}
            />

            <Column
              title={t('scenes.settings.super_users.scene.table.columns.email')}
              dataIndex="email"
              key="email"
            />

            <Column
              title={t(
                'scenes.settings.super_users.scene.table.columns.company'
              )}
              key="company"
              render={(row: User) => <span>{row.company.name}</span>}
            />

            <Column
              width={120}
              align="center"
              title={t(
                'scenes.settings.super_users.scene.table.columns.user_type'
              )}
              key="userType"
              render={(row: User) => (
                <Tag
                  color={
                    row.userType === USER_TYPES.SUPER_ADMIN ? 'blue' : 'purple'
                  }
                >
                  {getUserTypeLabel(row.userType)}
                </Tag>
              )}
            />

            <Column
              width={240}
              align="center"
              title={t('scenes.settings.super_users.scene.table.columns.role')}
              key="roles"
              render={(row: User) => (
                <>
                  {row.roles.length === 0 && (
                    <Tag color={row.disabledAt ? 'error' : 'default'}>
                      {t('scenes.settings.super_users.scene.table.role.none')}
                    </Tag>
                  )}
                  {row.roles.length === 1 && (
                    <Tag color={row.disabledAt ? 'error' : 'processing'}>
                      {row.roles[0].name}
                    </Tag>
                  )}

                  {row.roles.length > 1 && (
                    <Tooltip
                      title={`${row.roles
                        .map((record) => record.name)
                        .join(', ')}`}
                    >
                      <Tag
                        icon={<ExclamationCircleOutlined />}
                        style={{ cursor: 'pointer' }}
                        color={row.disabledAt ? 'error' : 'processing'}
                      >
                        {t(
                          'scenes.settings.super_users.scene.table.role.more_than_one'
                        )}
                      </Tag>
                    </Tooltip>
                  )}
                </>
              )}
            />

            <Column
              width={120}
              align="center"
              title=""
              key="actions"
              render={(row: User) => (
                <Dropdown
                  menu={{ items: getDropdownMenu(row) }}
                  placement="bottomLeft"
                  trigger={['click']}
                >
                  <Button size="small">
                    <MoreOutlined />
                  </Button>
                </Dropdown>
              )}
            />
          </Table>
        </Card>

        {/* Modals */}
        <NewUserModal
          isOpen={isNewResourceModalOpen}
          handleModalCancel={handleCloseNewResourceModal}
          submit={createNewUser}
          roles={roles}
          defaultCountryCode={getDialCode(authUser.company.countryCode)}
          companies={companies}
          countries={countries}
          getDialCode={getDialCode}
        />

        {selectedRow && (
          <DetailUserModal
            isOpen={isDetailModalOpen}
            id={selectedRow.id}
            handleModalCancel={handleCloseShowDetailResourceModal}
            submit={editUser}
            roles={roles}
            defaultCountryCode={getDialCode(authUser.company.countryCode)}
            countries={countries}
            getDialCode={getDialCode}
          />
        )}
      </Layout>
    </>
  );
}

export default SuperUsers;
